import React, { useRef, useState } from 'react';
import { Button, Stack, Progress, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { UuidString } from '~src/utils/UUID';
import AvatarWithFallback from './AvatarWithFallback';

interface ProfileImageUploaderProps {
  currentImageUrl: string | null;
  seedId: string;
  onImageUploaded: (uploadId: UuidString) => void;
  uploadFunction: (
    file: File,
    onProgress: (progress: number | undefined) => void,
    signal: AbortSignal,
  ) => Promise<UuidString>;
}

type UploaderState =
  | { status: 'idle' }
  | { status: 'uploading'; progress: number }
  | { status: 'error'; error: string };

const ALLOWED_TYPES = ['image/jpeg', 'image/png'];

export default function ProfileImageUploader({
  currentImageUrl,
  seedId,
  onImageUploaded,
  uploadFunction,
}: ProfileImageUploaderProps): JSX.Element {
  const [state, setState] = useState<UploaderState>({ status: 'idle' });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      notifications.show({
        title: 'Invalid File Type',
        message: 'Please select a JPEG or PNG image. Videos are not allowed.',
        color: 'red',
      });
      // Reset the input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Start upload
    setState({ status: 'uploading', progress: 0 });
    abortControllerRef.current = new AbortController();

    try {
      const uploadId = await uploadFunction(
        file,
        (progress) => {
          if (progress !== undefined) {
            setState({ status: 'uploading', progress });
          }
        },
        abortControllerRef.current.signal,
      );

      setState({ status: 'idle' });
      onImageUploaded(uploadId);
      notifications.show({
        title: 'Upload Successful',
        message: 'Image uploaded successfully',
        color: 'green',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setState({ status: 'error', error: errorMessage });
      notifications.show({
        title: 'Upload Failed',
        message: errorMessage,
        color: 'red',
      });
    } finally {
      // Reset the input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      abortControllerRef.current = null;
    }
  };

  const handleButtonClick = (): void => {
    fileInputRef.current?.click();
  };

  return (
    <Stack align="center" gap="md">
      <AvatarWithFallback imageUrl={currentImageUrl} seedId={seedId} size={100} />

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="image/jpeg,image/png"
        style={{ display: 'none' }}
      />

      <Button onClick={handleButtonClick} disabled={state.status === 'uploading'} variant="light">
        {state.status === 'uploading' ? 'Uploading...' : 'Choose Image'}
      </Button>

      {state.status === 'uploading' && <Progress value={state.progress} style={{ width: '100%' }} />}

      {state.status === 'error' && (
        <Text c="red" size="sm">
          {state.error}
        </Text>
      )}
    </Stack>
  );
}
